import React, { useState, useEffect, useRef, FC } from "react";
import { Play, Pause, Trash2, Plus, Loader2, Download, Copy } from "lucide-react";
import type { GeneratedVoice } from "./constants";

interface GeneratedVoiceItemProps {
    voiceData: GeneratedVoice;
    onDelete: (id: string) => void;
    onAddToTimeline: (item: any, category: any) => void;
    onCopyText?: (text: string) => void;
}

const GeneratedVoiceItem: FC<GeneratedVoiceItemProps> = ({ voiceData, onDelete, onAddToTimeline, onCopyText }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
        if (voiceData.url && !voiceData.isLoading) {
            const audio = new ((window as any).Audio)(voiceData.url);
            audio.preload = 'auto';
            audio.onended = () => setIsPlaying(false);
            audio.onpause = () => setIsPlaying(false);
            audioRef.current = audio;
        }

        return () => {
            if (audioRef.current) {
                (audioRef.current as any).pause();
                (audioRef.current as any).src = '';
                audioRef.current = null;
            }
        };
    }, [voiceData.url, voiceData.isLoading]);

    const togglePlay = () => {
        if (!audioRef.current || voiceData.isLoading) return;
        if (isPlaying) {
            (audioRef.current as any).pause();
        } else {
            (audioRef.current as any).currentTime = 0;
            (audioRef.current as any).play().catch(console.error);
        }
        setIsPlaying(!isPlaying);
    };

    const handleDragStart = (e: React.DragEvent) => {
        if (voiceData.isLoading || !voiceData.url || !voiceData.duration) {
            e.preventDefault();
            return;
        }
        const dragData = {
            type: 'audio',
            name: `Voiceover: "${voiceData.text.substring(0, 20)}..."`,
            url: voiceData.url,
            duration: voiceData.duration,
            originalDuration: voiceData.duration,
        };
        (e.dataTransfer as any).setData('application/json', JSON.stringify(dragData));
        (e.dataTransfer as any).effectAllowed = 'copy';
    };

    if (voiceData.isLoading) {
        return (
            <div className="w-full flex items-center justify-center gap-3 p-3 rounded-xl border border-dashed border-gray-300 bg-gray-50">
                <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                <span className="text-sm text-gray-600">Génération en cours...</span>
            </div>
        )
    }

    return (
        <div
            draggable={!voiceData.isLoading && !!voiceData.url}
            onDragStart={handleDragStart}
            className={`w-full flex items-center gap-3 p-2 rounded-xl border ${!voiceData.url ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white cursor-grab active:cursor-grabbing'}`}
        >
            <button onClick={togglePlay} disabled={!voiceData.url} className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition disabled:opacity-50 disabled:cursor-not-allowed">
                {isPlaying ? <Pause className="w-4 h-4 text-gray-800" /> : <Play className="w-4 h-4 text-gray-800 ml-0.5" />}
            </button>
            <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${!voiceData.url ? 'text-red-700' : 'text-gray-800'} truncate`} title={voiceData.text}>
                    "{voiceData.text}"
                </p>
                <div className="flex items-center gap-2">
                    <p className="text-xs text-gray-500">{voiceData.voice}</p>
                    {voiceData.duration && (
                        <span className="text-xs text-gray-400">• {Math.round(voiceData.duration)}s</span>
                    )}
                </div>
            </div>
            <div className="flex-shrink-0 flex items-center gap-1">
                {onCopyText && (
                    <button
                        onClick={() => onCopyText(voiceData.text)}
                        disabled={!voiceData.url}
                        className="p-2 text-gray-500 hover:bg-gray-100 hover:text-black rounded-full transition disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Copier le texte"
                    >
                        <Copy className="w-4 h-4" />
                    </button>
                )}
                <button
                    onClick={() => onAddToTimeline(voiceData, { id: 'voiceover', name: 'Voiceover' })}
                    disabled={!voiceData.url}
                    className="p-2 text-gray-500 hover:bg-gray-100 hover:text-black rounded-full transition disabled:opacity-50 disabled:cursor-not-allowed" title="Ajouter à la timeline">
                    <Plus className="w-4 h-4" />
                </button>
                <a
                    href={voiceData.url || '#'}
                    download={`LVMH-Voiceover-${voiceData.text.substring(0, 25).replace(/[^a-zA-Z0-9]/g, '_')}.mp3`}
                    className={`p-2 text-gray-500 hover:bg-gray-100 hover:text-black rounded-full transition ${!voiceData.url ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                    title="Télécharger"
                    onClick={(e) => { if (!voiceData.url) e.preventDefault(); }}
                >
                    <Download className="w-4 h-4" />
                </a>
                <button
                    onClick={() => onDelete(voiceData.id)}
                    className="p-2 text-gray-500 hover:bg-red-50 hover:text-red-600 rounded-full transition" title="Supprimer">
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

export default GeneratedVoiceItem;
